import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bar as BarChart } from 'react-chartjs-2';
import {
  Activity, AlertTriangle, ArrowLeft, ArrowRight, Ban, BellRing, Bot, Building2, CalendarClock, CheckCircle2, ClipboardList,
  Check, Copy, Database, FileText, HardDrive, History, Layers, Mail, Minus, Pencil, Plus, Power, Radar, ShieldAlert, SlidersHorizontal, Sparkles, Trash2, UserCheck, UserMinus, Users, Wrench,
} from 'lucide-react';
import api, { errorText } from '../services/api';
import { baseOptions } from '../lib/charts';
import { useAccess } from '../context/AuthContext';
import { useApi } from '../lib/useApi';
import { actionLabel, ago, bytes, date, dateTime, daysUntil, limit, num } from '../lib/format';
import {
  Bar, Button, Card, Chip, Empty, ErrorBanner, Field, Input, Modal, PageSkeleton, PLAN_TONE, Select, Stat, Tabs, TenantLogo, Textarea, Tone, useToast,
} from '../components/ui';
import { FLAG_INFO, overrideText, PlanInfo, SOURCE_INFO, SourceKind, statusChip } from './Tenants';

interface Detail {
  tenant: { id: string; company_name: string; subdomain: string; is_active: boolean; created_at: string; updated_at: string | null; trial_start_date: string | null;
            domain: string | null; logo_url: string | null; fluxpilot_enabled: boolean; ai_mode: 'byok' | 'platform';
            consent: { terms_accepted: boolean; terms_accepted_at: string | null; marketing_consent: boolean; marketing_consent_at: string | null };
            sizing: { company_size: string | null; vendors_managed: string | null } };
  plan: PlanInfo;
  usage: {
    users: number; active_users: number; vendors: number; last_login_at: string | null; vendors_pct: number | null; users_pct: number | null;
    scans_total: number; scans_30d: number; last_scan_at: string | null; open_alerts: number; open_remediation: number;
    pending_requests: number; active_schedules: number; risk_levels: Record<string, number>; scans_by_month: { month: string; scans: number }[];
  };
  flags: string[];
  storage: { database_bytes: number; file_bytes: number; files: number; rows: number; total_bytes: number;
             areas: { area: string; bytes: number }[]; computed_at: number };
  my_organization: { name: string; domain: string; score: number | null } | null;
  users: { id: string; name: string; email: string; role: string; is_active: boolean; quarantined: boolean; quarantine_reason: string | null;
           quarantined_at: string | null; last_login_at: string | null; created_at: string; job_title: string | null }[];
  vendors: { id: string; name: string; domain: string; score: number | null; risk_level: string | null; lifecycle: string | null; status: string | null; last_scan: string | null }[];
  activity: { id: number; action: string; details: string | null; created_at: string; actor: string | null; actor_email: string | null }[];
  plan_history: { id: number; old_plan: any; new_plan: any; reason: string | null; changed_by_email: string | null; changed_at: string }[];
  source: {
    kind: SourceKind; created_by: string | null; note: string | null;
    invited_by: { id: string; company_name: string } | null; portal_account: { id: string; email: string; name: string | null } | null;
  };
  invite: { user_id: string; email: string; name: string; accepted: boolean; accepted_at: string | null } | null;
}
interface Preset { key: string; label: string; plan: string; max_vendors: number; max_users: number; max_indicators: number }

type Tab = 'overview' | 'users' | 'vendors' | 'plan' | 'activity' | 'danger';
const RISK_TONE: Record<string, Tone> = { Critical: 'red', High: 'orange', Medium: 'amber', Low: 'green', 'Not scanned': 'slate' };
const ROLE_LABEL: Record<string, string> = { admin: 'Admin', risk_analyst: 'Risk analyst', viewer: 'Viewer', super_admin: 'Super admin' };

export default function TenantDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { canWrite } = useAccess();
  const [tab, setTab] = useState<Tab>('overview');
  const [modal, setModal] = useState<null | 'plan' | 'trial' | 'status' | 'rename' | 'invite' | 'fluxpilot' | 'ai'>(null);
  const { data: d, error, loading, reload } = useApi<Detail>(`/tenants/${id}`);

  if (loading) return <PageSkeleton />;
  if (error || !d) return <ErrorBanner text={error || 'Tenant not found'} onRetry={reload} />;
  const t = d.tenant;
  const trialDays = daysUntil(d.plan.trial_end_date);
  const done = (msg: string) => { toast('ok', msg); setModal(null); reload(); };

  return (
    <div>
      <button onClick={() => navigate('/tenants')} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Tenants
      </button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <TenantLogo name={t.company_name} domain={t.domain} logoUrl={t.logo_url} size={48} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold text-slate-900">{t.company_name}</h1>
              {canWrite && <button onClick={() => setModal('rename')} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Rename"><Pencil className="h-3.5 w-3.5" /></button>}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Chip tone={PLAN_TONE[d.plan.label] || 'slate'}>{d.plan.label}</Chip>
              {statusChip(t.is_active, d.plan)}
              {t.fluxpilot_enabled && <Chip tone="blue">FluxPilot on</Chip>}
              <Chip tone={t.ai_mode === 'platform' ? 'purple' : 'slate'}>{t.ai_mode === 'platform' ? 'FluxWatch-provided AI' : 'BYOK'}</Chip>
              {d.flags.filter(f => f !== 'inactive' && !f.startsWith('trial')).map(f => <Chip key={f} tone={FLAG_INFO[f]?.tone}>{FLAG_INFO[f]?.label || f}</Chip>)}
              <span className="text-xs text-slate-500">· {t.domain || t.subdomain} · created {date(t.created_at)}</span>
            </div>
            {d.source && <SourceLine source={d.source} />}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite ? (<>
            <Button icon={Layers} variant="primary" onClick={() => setModal('plan')}>Change plan</Button>
            {d.plan.plan_status === 'trial' && <Button icon={CalendarClock} onClick={() => setModal('trial')}>Extend trial</Button>}
            <Button icon={Bot} onClick={() => setModal('fluxpilot')}
              title="Off by default for every tenant -- the only way to turn it on is here">
              {t.fluxpilot_enabled ? 'Turn off FluxPilot' : 'Turn on FluxPilot'}
            </Button>
            {t.ai_mode === 'platform' && (
              <Button icon={Sparkles} onClick={() => setModal('ai')} title="Change the provider, model or key FluxWatch uses for this tenant">
                Edit AI settings
              </Button>
            )}
            <Button icon={Power} onClick={() => setModal('status')}>{t.is_active ? 'Deactivate' : 'Reactivate'}</Button>
          </>) : <Chip tone="slate">Read-only access</Chip>}
        </div>
      </div>

      {d.invite && !d.invite.accepted && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <span className="flex items-center gap-2"><Mail className="h-4 w-4" />
            Waiting for {d.invite.name || d.invite.email} ({d.invite.email}) to set their password and sign in for the first time.
          </span>
          {canWrite && <button onClick={() => setModal('invite')} className="font-medium underline-offset-2 hover:underline">Resend invite</button>}
        </div>
      )}
      {d.invite && d.invite.accepted && d.source.kind === 'admin' && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
          <Check className="h-4 w-4" /> Activated: {d.invite.name || d.invite.email} first signed in {date(d.invite.accepted_at)}.
        </div>
      )}
      {!t.is_active && (
        <div className="mb-5 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-700">
          <Ban className="h-4 w-4" /> This workspace is deactivated. Its users can't sign in until you reactivate it.
        </div>
      )}
      {d.plan.plan_status === 'trial' && trialDays !== null && (
        <div className={`mb-5 flex items-center justify-between gap-2 rounded-lg border px-4 py-3 text-sm ${trialDays < 0 ? 'border-red-200 bg-red-50 text-red-700' : trialDays <= 7 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-blue-100 bg-blue-50 text-blue-800'}`}>
          <span className="flex items-center gap-2"><CalendarClock className="h-4 w-4" />
            {trialDays < 0 ? `Trial ended ${Math.abs(trialDays)} day${Math.abs(trialDays) === 1 ? '' : 's'} ago (${date(d.plan.trial_end_date)}).` : `Trial ends in ${trialDays} day${trialDays === 1 ? '' : 's'} (${date(d.plan.trial_end_date)}).`}
          </span>
          {canWrite && <button onClick={() => setModal('trial')} className="font-medium underline-offset-2 hover:underline">Extend</button>}
        </div>
      )}

      <Tabs value={tab} onChange={setTab} tabs={[
        { key: 'overview', label: 'Overview' }, { key: 'users', label: 'Users', count: d.users.length },
        { key: 'vendors', label: 'Vendors', count: d.usage.vendors }, { key: 'plan', label: 'Plan and history' },
        { key: 'activity', label: 'Activity' }, { key: 'danger', label: 'Danger zone' },
      ]} />

      <div className="mt-5">
        {tab === 'overview' && <OverviewTab d={d} />}
        {tab === 'users' && <UsersTab d={d} onChanged={reload} />}
        {tab === 'vendors' && <VendorsTab d={d} />}
        {tab === 'plan' && <PlanTab d={d} onChange={canWrite ? () => setModal('plan') : undefined} />}
        {tab === 'activity' && <ActivityTab d={d} />}
        {tab === 'danger' && <DangerTab d={d} />}
      </div>

      {modal === 'plan' && <PlanModal d={d} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'trial' && <TrialModal d={d} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'status' && <StatusModal d={d} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'fluxpilot' && <FluxPilotModal d={d} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'ai' && <AIProvisioningModal d={d} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'rename' && <RenameModal d={d} onClose={() => setModal(null)} onDone={done} />}
      {modal === 'invite' && <ResendInviteModal d={d} onClose={() => { setModal(null); reload(); }} />}
    </div>
  );
}

// Where the workspace came from, under the name: self sign-up, the vendor portal (who, invited by which customer), or
// created by the team (by whom, and why).
function SourceLine({ source }: { source: Detail['source'] }) {
  const info = SOURCE_INFO[source.kind] || SOURCE_INFO.self;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
      <Chip tone={info.tone}>{info.label}</Chip>
      {source.kind === 'admin' && <span>by {source.created_by || 'the FluxWatch team'}{source.note ? ` · "${source.note}"` : ''}</span>}
      {source.kind === 'vendor_portal' && (
        <span>
          {source.portal_account ? <>claimed by {source.portal_account.name || source.portal_account.email} ({source.portal_account.email})</> : 'claimed from the portal'}
          {source.invited_by ? <> · first invited by <Link to={`/tenants/${source.invited_by.id}`} className="text-blue-600 hover:underline">{source.invited_by.company_name}</Link></> : ''}
        </span>
      )}
    </div>
  );
}

function ResendInviteModal({ d, onClose }: { d: Detail; onClose: () => void }) {
  const [send, setSend] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [res, setRes] = useState<{ link: string; email_sent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const go = async () => {
    setBusy(true); setErr(null);
    try { setRes((await api.post(`/tenants/${d.tenant.id}/invite/resend`, { send_email: send })).data); }
    catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Resend invite" onClose={onClose}
      footer={res ? <Button variant="primary" onClick={onClose}>Done</Button>
        : <><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} onClick={go}>Make new link</Button></>}>
      {res ? (
        <div className="space-y-3 text-sm text-slate-700">
          <p>{res.email_sent ? `Emailed to ${d.invite?.email}. ` : ''}The previous link no longer works. This one works once and expires in 7 days.</p>
          <div className="flex gap-2">
            <Input readOnly value={res.link} onFocus={e => e.currentTarget.select()} className="font-mono text-xs" />
            <Button icon={copied ? Check : Copy} onClick={async () => { try { await navigator.clipboard.writeText(res.link); setCopied(true); } catch { /* ignore */ } }}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3 text-sm text-slate-700">
          <p>Makes a new set-password link for <b>{d.invite?.email}</b>. Any earlier link stops working.</p>
          <label className="flex items-center gap-2"><input type="checkbox" checked={send} onChange={e => setSend(e.target.checked)} /> Email it to them with the welcome message</label>
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
      )}
    </Modal>
  );
}

/* ─── tabs ──────────────────────────────────────────────────────────────── */

function ConsentRow({ label, given, at, fallback }: { label: string; given: boolean; at: string | null; fallback?: string }) {
  const value = given ? `Yes · ${dateTime(at)}` : (fallback || 'No');
  return <Row label={label} value={value} tone={given ? 'green' : undefined} />;
}

function OverviewTab({ d }: { d: Detail }) {
  const u = d.usage;
  const months = u.scans_by_month.map(m => new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'short' }));
  const risk = Object.entries(u.risk_levels);
  const totalRisk = risk.reduce((s, [, n]) => s + n, 0) || 1;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <UsageTile label="Vendors" used={u.vendors} max={d.plan.max_vendors} pct={u.vendors_pct} icon={Building2} tone="blue" />
        <UsageTile label="Users" used={u.users} max={d.plan.max_users} pct={u.users_pct} icon={Users} tone="purple" sub={`${num(u.active_users)} active`} />
        <Stat label="Scans" value={num(u.scans_30d)} icon={Radar} tone="green" sub={`last 30 days · ${num(u.scans_total)} total · last ${ago(u.last_scan_at)}`} />
        <Stat label="Checks per scan" value={d.plan.all_indicators ? num(d.plan.indicators_total) : `${num(d.plan.indicators_included)} of ${num(d.plan.indicators_total)}`}
          icon={CheckCircle2} tone={d.plan.all_indicators ? 'teal' : 'amber'} sub={d.plan.all_indicators ? 'Every check' : 'Core checks only'} />
        <Stat label="Storage used" value={bytes(d.storage.total_bytes)} icon={HardDrive} tone="orange"
          sub={`${bytes(d.storage.database_bytes)} data · ${bytes(d.storage.file_bytes)} files`} />
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
        <Card title="Scans per month" icon={Activity} tone="green" right={<span className="text-xs text-slate-400">Last 12 months</span>}>
          <div className="h-52"><BarChart data={{ labels: months, datasets: [{ data: u.scans_by_month.map(m => m.scans), backgroundColor: '#10b981', borderRadius: 4, maxBarThickness: 26 }] }} options={baseOptions()} /></div>
        </Card>
        <Card title="Workspace snapshot" icon={ClipboardList} tone="slate" bodyClass="p-0">
          <ul className="divide-y divide-slate-100 text-sm">
            <Row label="Last sign-in" value={ago(u.last_login_at)} />
            <Row label="Company size" value={d.tenant.sizing?.company_size ? `${d.tenant.sizing.company_size} employees` : 'Not asked'} />
            <Row label="Vendors they manage" value={d.tenant.sizing?.vendors_managed ? `${d.tenant.sizing.vendors_managed} vendors` : 'Not asked'} />
            <Row label="Their own company" value={d.my_organization ? `${d.my_organization.name}${d.my_organization.score != null ? ` · ${d.my_organization.score}` : ''}` : 'Not set up'} />
            <Row label="Open alerts" value={num(u.open_alerts)} tone={u.open_alerts ? 'amber' : undefined} />
            <Row label="Open remediation" value={num(u.open_remediation)} />
            <Row label="Requests awaiting review" value={num(u.pending_requests)} />
            <Row label="Scheduled vendors" value={num(u.active_schedules)} />
            <ConsentRow label="Terms & Privacy Policy accepted" given={d.tenant.consent.terms_accepted} at={d.tenant.consent.terms_accepted_at}
              fallback={d.source.kind === 'admin' ? 'N/A · created by admin' : 'No'} />
            <ConsentRow label="Marketing opt-in" given={d.tenant.consent.marketing_consent} at={d.tenant.consent.marketing_consent_at} />
          </ul>
          <div className="border-t border-slate-100 px-5 py-4">
            <div className="mb-2 text-xs font-medium text-slate-500">Vendors by risk</div>
            {risk.length === 0 ? <div className="text-sm text-slate-400">No vendors yet</div> : (
              <>
                <div className="flex h-2.5 overflow-hidden rounded-full">
                  {risk.map(([k, n]) => <div key={k} style={{ flex: n }} className={{ red: 'bg-red-500', orange: 'bg-orange-500', amber: 'bg-amber-400', green: 'bg-emerald-500', slate: 'bg-slate-300' }[RISK_TONE[k] as string] || 'bg-slate-300'} />)}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">{risk.map(([k, n]) => <Chip key={k} tone={RISK_TONE[k] || 'slate'}>{k} {n} · {Math.round(n / totalRisk * 100)}%</Chip>)}</div>
              </>
            )}
          </div>
        </Card>
      </div>
      <StorageCard d={d} />
    </div>
  );
}

const AREA_COLOR: Record<string, string> = { Scans: 'bg-emerald-500', Vendors: 'bg-blue-500', Questionnaires: 'bg-violet-500', Remediation: 'bg-amber-500', Alerting: 'bg-red-400', 'Audit log': 'bg-slate-400', 'Users and access': 'bg-teal-500', Other: 'bg-slate-300' };

function StorageCard({ d }: { d: Detail }) {
  const s = d.storage;
  const total = s.total_bytes || 1;
  const filesPct = Math.round(s.file_bytes / total * 100);
  return (
    <Card title="Storage" icon={HardDrive} tone="orange" right={<span className="text-xs text-slate-400">Updated {ago(new Date(s.computed_at * 1000).toISOString())}</span>}>
      {s.total_bytes === 0 ? <div className="text-sm text-slate-500">This workspace hasn't stored anything yet.</div> : (
        <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <div className="text-3xl font-semibold tracking-tight text-slate-900">{bytes(s.total_bytes)}</div>
            <div className="mt-1 text-xs text-slate-500">{num(s.rows)} records · {num(s.files)} uploaded file{s.files === 1 ? '' : 's'}</div>
            <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
              <div className="bg-blue-500" style={{ width: `${100 - filesPct}%` }} />
              <div className="bg-orange-400" style={{ width: `${filesPct}%` }} />
            </div>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-slate-600"><Database className="h-3.5 w-3.5 text-blue-500" />Database</span><span className="font-medium text-slate-900">{bytes(s.database_bytes)}</span></div>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 text-slate-600"><FileText className="h-3.5 w-3.5 text-orange-500" />Files (contracts, evidence)</span><span className="font-medium text-slate-900">{bytes(s.file_bytes)}</span></div>
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-medium text-slate-500">Database by area</div>
            <ul className="space-y-2.5">
              {s.areas.map(a => (
                <li key={a.area}>
                  <div className="flex justify-between text-sm"><span className="text-slate-700">{a.area}</span><span className="text-slate-500">{bytes(a.bytes)} <span className="text-xs">· {Math.round(a.bytes / (s.database_bytes || 1) * 100)}%</span></span></div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${AREA_COLOR[a.area] || 'bg-slate-300'}`} style={{ width: `${a.bytes / (s.areas[0]?.bytes || 1) * 100}%` }} /></div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <p className="mt-4 text-xs text-slate-400">File sizes are exact. Database size is estimated from this workspace's own rows; indexes and shared reference data aren't included.</p>
    </Card>
  );
}

function UsageTile({ label, used, max, pct, icon, tone, sub }: { label: string; used: number; max: number; pct: number | null; icon: React.ElementType; tone: Tone; sub?: string }) {
  return (
    <Stat label={label} icon={icon} tone={tone} value={<span>{num(used)} <span className="text-sm font-normal text-slate-400">/ {limit(max)}</span></span>}
      sub={<span className="block">{max === -1 ? 'No limit' : <Bar pct={pct} />}{sub && <span className="mt-1 block">{sub}</span>}</span>} />
  );
}

function Row({ label, value, tone }: { label: string; value: React.ReactNode; tone?: Tone }) {
  return (
    <li className="flex items-center justify-between gap-3 px-5 py-2.5">
      <span className="text-slate-500">{label}</span>
      {tone ? <Chip tone={tone}>{value}</Chip> : <span className="truncate font-medium text-slate-900">{value}</span>}
    </li>
  );
}

function UsersTab({ d, onChanged }: { d: Detail; onChanged: () => void }) {
  const toast = useToast();
  const { canWrite } = useAccess();
  const [block, setBlock] = useState<Detail['users'][0] | null>(null);
  const [remove, setRemove] = useState<Detail['users'][0] | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true); setErr(null);
    try { await fn(); toast('ok', msg); setBlock(null); setRemove(null); setReason(''); onChanged(); }
    catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };
  const unblock = async (u: Detail['users'][0]) => {
    try { await api.post(`/tenants/${d.tenant.id}/users/${u.id}/unquarantine`); toast('ok', `${u.name} can sign in again`); onChanged(); }
    catch (e) { toast('error', errorText(e)); }
  };

  return (
    <Card bodyClass="p-0">
      {d.users.length === 0 ? <Empty icon={Users} title="No users" /> : (
        <table className="min-w-full">
          <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
            {['User', 'Role', 'Status', 'Last sign-in', 'Joined', ''].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {d.users.map(u => (
              <tr key={u.id}>
                <td className="px-4 py-3"><div className="text-sm font-medium text-slate-900">{u.name}</div><div className="text-xs text-slate-500">{u.email}{u.job_title ? ` · ${u.job_title}` : ''}</div></td>
                <td className="px-4 py-3"><Chip tone={u.role === 'admin' ? 'blue' : 'slate'}>{ROLE_LABEL[u.role] || u.role}</Chip></td>
                <td className="px-4 py-3">
                  {u.quarantined ? <span title={u.quarantine_reason || ''}><Chip tone="red" dot>Blocked</Chip><div className="mt-1 max-w-[200px] truncate text-xs text-slate-500">{u.quarantine_reason}</div></span>
                    : u.is_active ? <Chip tone="green" dot>Active</Chip> : <Chip tone="slate" dot>Inactive</Chip>}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{ago(u.last_login_at)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{date(u.created_at)}</td>
                <td className="px-4 py-3">
                  {canWrite && <div className="flex justify-end gap-1.5">
                    {u.quarantined
                      ? <Button size="sm" icon={UserCheck} onClick={() => unblock(u)}>Unblock</Button>
                      : u.is_active && <Button size="sm" icon={ShieldAlert} onClick={() => { setBlock(u); setErr(null); }}>Block</Button>}
                    <Button size="sm" variant="ghost" icon={UserMinus} onClick={() => { setRemove(u); setErr(null); }} aria-label="Remove user" />
                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={!!block} title={`Block ${block?.name}`} onClose={() => setBlock(null)}
        footer={<><Button onClick={() => setBlock(null)}>Cancel</Button>
          <Button variant="danger" loading={busy} disabled={reason.trim().length < 3}
            onClick={() => run(() => api.post(`/tenants/${d.tenant.id}/users/${block!.id}/quarantine`, { reason: reason.trim() }), `${block!.name} was blocked and signed out`)}>Block user</Button></>}>
        <p className="mb-4 text-sm text-slate-600">They're signed out straight away and can't sign in until you unblock them. Their data and history stay.</p>
        <Field label="Reason (shown in the tenant's audit log)" error={err}><Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Suspicious sign-ins from an unknown country" /></Field>
      </Modal>

      <Modal open={!!remove} title={`Remove ${remove?.name}`} onClose={() => setRemove(null)}
        footer={<><Button onClick={() => setRemove(null)}>Cancel</Button>
          <Button variant="danger" loading={busy} onClick={() => run(() => api.delete(`/tenants/${d.tenant.id}/users/${remove!.id}`), `${remove!.name} was removed`)}>Remove user</Button></>}>
        <p className="text-sm text-slate-600">This permanently deletes their account. If they own records (vendor requests, questionnaires) the removal is refused. Block them instead to keep that history.</p>
        {err && <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      </Modal>
    </Card>
  );
}

function VendorsTab({ d }: { d: Detail }) {
  return (
    <Card bodyClass="p-0">
      {d.vendors.length === 0 ? <Empty icon={Building2} title="No vendors yet" text="This workspace hasn't added any vendors." /> : (
        <table className="min-w-full">
          <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
            {['Vendor', 'Score', 'Risk', 'Lifecycle', 'Last scan'].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {d.vendors.map(v => (
              <tr key={v.id}>
                <td className="px-4 py-3"><div className="text-sm font-medium text-slate-900">{v.name}</div><div className="text-xs text-slate-500">{v.domain}</div></td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-900">{v.score ?? '—'}</td>
                <td className="px-4 py-3">{v.risk_level ? <Chip tone={RISK_TONE[v.risk_level] || 'slate'}>{v.risk_level}</Chip> : <span className="text-xs text-slate-400">Not scanned</span>}</td>
                <td className="px-4 py-3 text-sm capitalize text-slate-600">{(v.lifecycle || '—').replace(/_/g, ' ')}</td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{ago(v.last_scan)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="border-t border-slate-100 px-5 py-2.5 text-xs text-slate-400">Read-only view. Vendors are managed by the tenant in FluxWatch.</div>
    </Card>
  );
}

function planLine(p: any) {
  if (!p) return '—';
  return `${p.label || p.plan} · ${limit(p.max_vendors)} vendors · ${limit(p.max_users)} users${p.plan_status ? ` · ${p.plan_status}` : ''}`;
}

function PlanTab({ d, onChange }: { d: Detail; onChange?: () => void }) {
  const p = d.plan;
  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_1.4fr]">
      <Card title="Current plan" icon={Layers} tone="amber" right={onChange ? <Button size="sm" onClick={onChange}>Change</Button> : null} bodyClass="p-0">
        <ul className="divide-y divide-slate-100 text-sm">
          <Row label="Plan" value={<Chip tone={PLAN_TONE[p.label] || 'slate'}>{p.label}</Chip>} />
          <Row label="Status" value={p.plan_status === 'trial' ? 'Trial' : 'Active'} />
          {p.plan_status === 'trial' && <Row label="Trial ends" value={date(p.trial_end_date)} />}
          {overrideText(p) && <Row label="Override" value={<Chip tone="purple">{overrideText(p)}</Chip>} />}
          <Row label="Vendor limit" value={p.extra_vendors ? `${limit(p.max_vendors)} (${limit(p.max_vendors - p.extra_vendors)} + ${p.extra_vendors})` : limit(p.max_vendors)} />
          <Row label="User limit" value={p.extra_users ? `${limit(p.max_users)} (${limit(p.max_users - p.extra_users)} + ${p.extra_users})` : limit(p.max_users)} />
          <Row label="Checks per scan" value={p.all_indicators ? `All ${num(p.indicators_total)}` : `${num(p.indicators_included)} of ${num(p.indicators_total)}`} />
        </ul>
      </Card>
      <Card title="Plan history" icon={History} tone="slate" bodyClass="p-0">
        {d.plan_history.length === 0 ? <Empty icon={History} title="No plan changes yet" text="Changes made from the admin app are recorded here with the reason." /> : (
          <ul className="divide-y divide-slate-100">
            {d.plan_history.map(h => (
              <li key={h.id} className="px-5 py-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-slate-500">{planLine(h.old_plan)}</span><ArrowRight className="h-3.5 w-3.5 text-slate-400" /><span className="font-medium text-slate-900">{planLine(h.new_plan)}</span>
                </div>
                {h.reason && <div className="mt-1 text-sm text-slate-600">"{h.reason}"</div>}
                <div className="mt-1 text-xs text-slate-400">{h.changed_by_email || 'Unknown'} · {dateTime(h.changed_at)}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function ActivityTab({ d }: { d: Detail }) {
  return (
    <Card title="Recent activity" icon={Activity} tone="slate" right={<span className="text-xs text-slate-400">Last 40 events</span>} bodyClass="p-0">
      {d.activity.length === 0 ? <Empty icon={Activity} title="No activity yet" /> : (
        <ul className="divide-y divide-slate-100">
          {d.activity.map(a => (
            <li key={a.id} className="flex items-start gap-3 px-5 py-3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900">{actionLabel(a.action)}</div>
                {a.details && <div className="truncate text-sm text-slate-600" title={a.details}>{a.details}</div>}
                <div className="text-xs text-slate-400">{a.actor || a.actor_email || 'System'} · {dateTime(a.created_at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DangerTab({ d }: { d: Detail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { canWrite } = useAccess();
  const [preview, setPreview] = useState<{ tables: Record<string, number>; total_rows: number } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try { setPreview((await api.get(`/tenants/${d.tenant.id}/delete-preview`)).data); setOpen(true); }
    catch (e) { toast('error', errorText(e)); }
    finally { setLoadingPreview(false); }
  };
  const del = async () => {
    setBusy(true); setErr(null);
    try { await api.post(`/tenants/${d.tenant.id}/delete`, { confirm_name: name, reason }); toast('ok', `${d.tenant.company_name} was permanently deleted`); navigate('/tenants'); }
    catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card title="Deactivate instead" icon={Power} tone="amber">
        <p className="text-sm text-slate-600">Deactivating signs everyone out and blocks sign-in, but keeps every vendor, scan and record. It can be undone at any time. Use it for unpaid accounts or a pause.</p>
      </Card>
      <section className="rounded-xl border border-red-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></span>
            <div>
              <div className="text-sm font-semibold text-slate-900">Permanently delete this workspace</div>
              <div className="mt-0.5 max-w-xl text-sm text-slate-600">Deletes the tenant, its users and every vendor, scan, questionnaire, remediation item and log entry. This can't be undone.</div>
            </div>
          </div>
          {canWrite ? <Button variant="danger" icon={Trash2} loading={loadingPreview} onClick={loadPreview}>Review and delete</Button>
            : <Chip tone="slate">Read-only access</Chip>}
        </div>
      </section>

      <Modal open={open} title={`Delete ${d.tenant.company_name}?`} onClose={() => setOpen(false)} width="max-w-xl"
        footer={<><Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="danger" loading={busy} disabled={name.trim() !== d.tenant.company_name.trim() || reason.trim().length < 3} onClick={del}>Delete permanently</Button></>}>
        {preview && (
          <>
            <div className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {num(preview.total_rows)} records across {Object.keys(preview.tables).length} tables will be deleted.
            </div>
            <div className="mb-4 grid max-h-48 grid-cols-2 gap-x-6 gap-y-1 overflow-y-auto rounded-lg border border-slate-100 p-3 text-xs">
              {Object.entries(preview.tables).map(([table, n]) => (
                <div key={table} className="flex justify-between"><span className="truncate text-slate-600">{table.replace(/_/g, ' ')}</span><span className="font-medium text-slate-900">{num(n)}</span></div>
              ))}
            </div>
          </>
        )}
        <div className="space-y-3">
          <Field label={`Type ${d.tenant.company_name} to confirm`}><Input value={name} onChange={e => setName(e.target.value)} autoComplete="off" /></Field>
          <Field label="Reason (kept in the platform audit log)" error={err}><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Customer asked to close their account" /></Field>
        </div>
      </Modal>
    </div>
  );
}

/* ─── modals ────────────────────────────────────────────────────────────── */

function useSubmit(onDone: (msg: string) => void) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (fn: () => Promise<string>) => {
    setBusy(true); setErr(null);
    try { onDone(await fn()); } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };
  return { busy, err, submit };
}

function PlanModal({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: (m: string) => void }) {
  const toast = useToast();
  const { data } = useApi<{ presets: Preset[]; indicator_total: number }>('/plans');
  const current = d.plan.tier || data?.presets.find(p => p.label === d.plan.label)?.key;
  const [choice, setChoice] = useState<string>('');
  // Override: extra vendors / users on top of a Strato, Meso or Thermo preset.
  const hasOverride = !!(d.plan.extra_vendors || d.plan.extra_users);
  const [override, setOverride] = useState({ open: hasOverride, vendors: d.plan.extra_vendors || 0, users: d.plan.extra_users || 0 });
  const [custom, setCustom] = useState({ plan: 'custom', vendors: String(d.plan.max_vendors), users: String(d.plan.max_users), all: d.plan.all_indicators, checks: String(d.plan.max_indicators), status: d.plan.plan_status });
  const [reason, setReason] = useState('');
  const { busy, err, submit } = useSubmit(onDone);
  const selected = choice || current || '';
  const preset = data?.presets.find(p => p.key === selected);
  const canOverride = OVERRIDABLE.includes(selected);
  const extras = canOverride && override.open ? { vendors: override.vendors, users: override.users } : { vendors: 0, users: 0 };

  const save = () => submit(async () => {
    const body: any = { reason: reason.trim() };
    if (selected === 'custom') {
      const num = (s: string) => (s.trim().toLowerCase() === 'unlimited' ? -1 : parseInt(s, 10));
      Object.assign(body, { plan: custom.plan, max_vendors: num(custom.vendors), max_users: num(custom.users),
        max_indicators: custom.all ? 226 : parseInt(custom.checks, 10), plan_status: custom.status });
    } else Object.assign(body, { preset: selected, extra_vendors: extras.vendors, extra_users: extras.users });
    const res = await api.put(`/tenants/${d.tenant.id}/plan`, body);
    (res.data.warnings || []).forEach((w: string) => toast('error', w));
    const extra = overrideText(res.data.plan);
    return `Plan changed to ${res.data.plan.label}${extra ? ` (${extra})` : ''}`;
  });

  return (
    <Modal open title="Change plan" onClose={onClose} width="max-w-2xl"
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} disabled={!selected || reason.trim().length < 3} onClick={save}>Save plan</Button></>}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {(data?.presets || []).map(p => (
          <button key={p.key} onClick={() => setChoice(p.key)}
            className={`rounded-lg border p-3 text-left transition ${selected === p.key ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
            <div className="flex items-center justify-between"><Chip tone={PLAN_TONE[p.label] || 'slate'}>{p.label}</Chip>{current === p.key && <span className="text-[11px] text-slate-400">Current</span>}</div>
            <div className="mt-2 text-xs text-slate-600">{limit(p.max_vendors)} vendors · {limit(p.max_users)} users</div>
            <div className="text-xs text-slate-500">{p.max_indicators >= 226 ? `All ${num(data?.indicator_total)} checks` : `${p.max_indicators} checks`}{p.plan === 'free' ? ' · trial' : ''}</div>
          </button>
        ))}
        <button onClick={() => setChoice('custom')}
          className={`rounded-lg border p-3 text-left transition ${selected === 'custom' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-dashed border-slate-300 hover:border-slate-400'}`}>
          <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800"><Wrench className="h-3.5 w-3.5" />Custom limits</div>
          <div className="mt-2 text-xs text-slate-500">Set vendors, users and checks yourself</div>
        </button>
      </div>
      {canOverride && preset && (
        <div className="mt-4 rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-800">Override</div>
              <div className="text-xs text-slate-500">Give this tenant a few extra vendors or users on top of {preset.label}, without moving them to a bigger plan.</div>
            </div>
            <Button size="sm" icon={SlidersHorizontal} variant={override.open ? 'primary' : 'secondary'}
              onClick={() => setOverride(o => ({ ...o, open: !o.open }))}>{override.open ? 'Remove override' : 'Override'}</Button>
          </div>
          {override.open && (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Stepper label="Extra vendors" value={override.vendors} base={preset.max_vendors} onChange={v => setOverride(o => ({ ...o, vendors: v }))} />
              <Stepper label="Extra users" value={override.users} base={preset.max_users} onChange={v => setOverride(o => ({ ...o, users: v }))} />
              <p className="text-xs text-slate-500 sm:col-span-2">
                The tenant keeps the {preset.label} name. Their limits become {num(preset.max_vendors + override.vendors)} vendors and {num(preset.max_users + override.users)} users.
              </p>
            </div>
          )}
        </div>
      )}
      {selected === 'custom' && (
        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-4">
          <Field label="Plan type"><Select value={custom.plan} onChange={e => setCustom({ ...custom, plan: e.target.value })}>
            <option value="free">Free (Tropos)</option><option value="custom">Custom</option><option value="pro">Pro (Exo)</option></Select></Field>
          <Field label="Status"><Select value={custom.status} onChange={e => setCustom({ ...custom, status: e.target.value })}><option value="active">Active</option><option value="trial">Trial</option></Select></Field>
          <Field label="Vendor limit" hint="A number, or 'unlimited'"><Input value={custom.vendors} onChange={e => setCustom({ ...custom, vendors: e.target.value })} /></Field>
          <Field label="User limit" hint="A number, or 'unlimited'"><Input value={custom.users} onChange={e => setCustom({ ...custom, users: e.target.value })} /></Field>
          <div className="col-span-2 flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={custom.all} onChange={e => setCustom({ ...custom, all: e.target.checked })} /> Every check ({num(data?.indicator_total)})</label>
            {!custom.all && <Input className="!w-28" value={custom.checks} onChange={e => setCustom({ ...custom, checks: e.target.value })} />}
          </div>
          <p className="col-span-2 text-xs text-slate-500">FluxWatch names custom plans by vendor limit: 100 = Strato, 500 = Meso, 1000 = Thermo.</p>
        </div>
      )}
      <div className="mt-4"><Field label="Reason (kept in plan history and the tenant's audit log)" error={err}><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Upgraded after signed order #1042" /></Field></div>
    </Modal>
  );
}

const OVERRIDABLE = ['strato', 'meso', 'thermo'];

function Stepper({ label, value, base, onChange }: { label: string; value: number; base: number; onChange: (v: number) => void }) {
  const set = (v: number) => onChange(Math.max(0, Math.min(100000, Number.isFinite(v) ? Math.floor(v) : 0)));
  return (
    <Field label={label} hint={`Plan includes ${num(base)} · new limit ${num(base + value)}`}>
      <div className="flex items-center gap-2">
        <Button size="sm" icon={Minus} onClick={() => set(value - 1)} disabled={value <= 0} aria-label={`Fewer ${label.toLowerCase()}`} />
        <Input className="!w-24 text-center" type="number" min={0} value={String(value)} onChange={e => set(parseInt(e.target.value, 10))} />
        <Button size="sm" icon={Plus} onClick={() => set(value + 1)} aria-label={`More ${label.toLowerCase()}`} />
      </div>
    </Field>
  );
}

function TrialModal({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: (m: string) => void }) {
  const [days, setDays] = useState('14');
  const [reason, setReason] = useState('');
  const { busy, err, submit } = useSubmit(onDone);
  const n = parseInt(days, 10);
  const base = Math.max(Date.now(), d.plan.trial_end_date ? new Date(d.plan.trial_end_date).getTime() : 0);
  return (
    <Modal open title="Extend trial" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} disabled={!(n >= 1 && n <= 365) || reason.trim().length < 3}
        onClick={() => submit(async () => { await api.post(`/tenants/${d.tenant.id}/trial/extend`, { days: n, reason: reason.trim() }); return `Trial extended by ${n} days`; })}>Extend</Button></>}>
      <div className="mb-3 flex gap-2">{['7', '14', '30'].map(v => <Button key={v} size="sm" variant={days === v ? 'primary' : 'secondary'} onClick={() => setDays(v)}>{v} days</Button>)}</div>
      <div className="space-y-3">
        <Field label="Days" hint={n >= 1 ? `New end date: ${date(new Date(base + n * 86400000).toISOString())}` : 'Between 1 and 365'}><Input type="number" min={1} max={365} value={days} onChange={e => setDays(e.target.value)} /></Field>
        <Field label="Reason" error={err}><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Evaluating with their security team" /></Field>
      </div>
    </Modal>
  );
}

function FluxPilotModal({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: (m: string) => void }) {
  const { busy, err, submit } = useSubmit(onDone);
  const turningOn = !d.tenant.fluxpilot_enabled;
  return (
    <Modal open title={turningOn ? `Turn on FluxPilot for ${d.tenant.company_name}?` : `Turn off FluxPilot for ${d.tenant.company_name}?`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant={turningOn ? 'primary' : 'danger'} loading={busy}
        onClick={() => submit(async () => {
          await api.post(`/tenants/${d.tenant.id}/fluxpilot`, { enabled: turningOn });
          return turningOn ? 'FluxPilot turned on for this tenant' : 'FluxPilot turned off for this tenant';
        })}>
        {turningOn ? 'Turn on' : 'Turn off'}</Button></>}>
      <p className="text-sm text-slate-600">
        {turningOn
          ? `Everyone at ${d.tenant.company_name} will see FluxPilot (the chat assistant) on every page, right away.`
          : `FluxPilot disappears from every page at ${d.tenant.company_name} immediately -- anyone with it open loses access mid-conversation.`}
      </p>
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
    </Modal>
  );
}

interface ProviderCatalog { [key: string]: { label: string; models: string[]; key_hint?: string; key_optional?: boolean } }
interface AIConfigState {
  mode: 'byok' | 'platform';
  platform: { provider_name: string; default_model: string | null; api_key_masked: string | null; is_active: boolean;
              last_tested_at: string | null; last_test_success: boolean | null; updated_at: string | null } | null;
}

/** Edit this tenant's FluxWatch-provided AI (model / key) -- only ever reachable for a tenant that was
 * already set up this way at creation. The BYOK-vs-platform choice itself is one-time and permanent
 * (per explicit user instruction): there is no switch here, and no way back to bring-your-own-key.
 * FluxWatch itself does the encryption, live model discovery and connection test
 * (core/ai_provisioning.py's proxy to FluxWatch's internal API) -- this modal only ever holds the
 * typed key in memory for this one request. */
function AIProvisioningModal({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: (m: string) => void }) {
  const [cfg, setCfg] = useState<AIConfigState | null>(null);
  const [catalog, setCatalog] = useState<ProviderCatalog | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [provider, setProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [discovered, setDiscovered] = useState<string[] | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const { busy, err, submit } = useSubmit((m) => { onDone(m); });

  React.useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get(`/tenants/${d.tenant.id}/ai-config`), api.get('/ai-provider-catalog')]);
        setCfg(c.data);
        setCatalog(p.data.providers);
        if (c.data.platform) { setProvider(c.data.platform.provider_name); setModel(c.data.platform.default_model || ''); }
      } catch (e) { setLoadErr(errorText(e)); }
    })();
  }, [d.tenant.id]);

  const providerInfo = provider && catalog ? catalog[provider] : null;

  const discover = async () => {
    if (!provider || !apiKey.trim()) return;
    setDiscovering(true); setDiscovered(null);
    try {
      const res = await api.post('/ai-model-discovery', { provider_name: provider, api_key: apiKey.trim() });
      setDiscovered(res.data.models || []);
      if (!model && res.data.models?.length) setModel(res.data.models[0]);
    } catch { setDiscovered(providerInfo?.models || []); } finally { setDiscovering(false); }
  };

  const save = () => submit(async () => {
    await api.post(`/tenants/${d.tenant.id}/ai-config`, { provider_name: provider, api_key: apiKey.trim(), default_model: model || null });
    return `Updated FluxWatch-provided AI for ${d.tenant.company_name} (${catalog?.[provider]?.label || provider}${model ? `, ${model}` : ''})`;
  });

  if (loadErr) {
    return <Modal open title="FluxWatch-provided AI" onClose={onClose}><p className="text-sm text-red-600">{loadErr}</p></Modal>;
  }
  if (!cfg || !catalog) {
    return <Modal open title="FluxWatch-provided AI" onClose={onClose}><p className="text-sm text-slate-500">Loading…</p></Modal>;
  }

  if (!editing && cfg.platform) {
    const p = cfg.platform;
    return (
      <Modal open title={`FluxWatch-provided AI · ${d.tenant.company_name}`} onClose={onClose}
        footer={<><Button onClick={onClose}>Close</Button><Button variant="primary" onClick={() => { setEditing(true); setApiKey(''); setDiscovered(null); }}>Change model or key</Button></>}>
        <div className="space-y-2 text-sm text-slate-700">
          <p>FluxWatch provides this tenant's AI -- their own Settings &gt; AI Providers page is hidden. This was chosen once at creation and can't be switched back to bring-your-own-key.</p>
          <Row label="Provider" value={catalog[p.provider_name]?.label || p.provider_name} />
          <Row label="Model" value={p.default_model || '—'} />
          <Row label="Key" value={p.api_key_masked || '—'} />
          <Row label="Last tested" value={p.last_tested_at ? `${ago(p.last_tested_at)} · ${p.last_test_success ? 'OK' : 'failed'}` : 'Never'} />
        </div>
      </Modal>
    );
  }

  return (
    <Modal open title={`FluxWatch-provided AI · ${d.tenant.company_name}`} onClose={onClose}
      footer={<>
        {cfg.platform && <Button onClick={() => setEditing(false)}>Back</Button>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} disabled={!provider || !apiKey.trim()} onClick={save}>Save</Button>
      </>}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Change the provider, model or key FluxWatch uses for {d.tenant.company_name}. Enter the current or a new key -- it's tested before saving.</p>
        <Field label="Provider">
          <Select value={provider} onChange={e => { setProvider(e.target.value); setModel(''); setDiscovered(null); }}>
            <option value="">Select a provider…</option>
            {Object.entries(catalog).map(([key, info]) => <option key={key} value={key}>{info.label}</option>)}
          </Select>
        </Field>
        {provider && (
          <Field label="API key" hint={providerInfo?.key_hint}>
            <div className="flex gap-2">
              <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder={providerInfo?.key_hint || 'API key'} className="flex-1" />
              <Button size="sm" loading={discovering} disabled={!apiKey.trim()} onClick={discover}>Discover models</Button>
            </div>
          </Field>
        )}
        {provider && (discovered || providerInfo?.models?.length) ? (
          <Field label="Default model">
            <Select value={model} onChange={e => setModel(e.target.value)}>
              <option value="">{discovered ? 'Select a model…' : 'Discover models first, or pick a curated one…'}</option>
              {(discovered ?? providerInfo?.models ?? []).map(m => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
        ) : null}
        {err && <p className="text-sm text-red-600">{err}</p>}
      </div>
    </Modal>
  );
}

function StatusModal({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: (m: string) => void }) {
  const [reason, setReason] = useState('');
  const { busy, err, submit } = useSubmit(onDone);
  const activating = !d.tenant.is_active;
  return (
    <Modal open title={activating ? `Reactivate ${d.tenant.company_name}` : `Deactivate ${d.tenant.company_name}`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant={activating ? 'primary' : 'danger'} loading={busy} disabled={reason.trim().length < 3}
        onClick={() => submit(async () => { await api.post(`/tenants/${d.tenant.id}/status`, { active: activating, reason: reason.trim() }); return activating ? 'Workspace reactivated' : 'Workspace deactivated and everyone signed out'; })}>
        {activating ? 'Reactivate' : 'Deactivate'}</Button></>}>
      <p className="mb-4 text-sm text-slate-600">
        {activating ? 'Its users will be able to sign in again. Nothing else changes.' : `All ${d.usage.users} users are signed out now and can't sign in until you reactivate it. No data is deleted; scheduled scans keep their settings.`}
      </p>
      <Field label="Reason" error={err}><Input value={reason} onChange={e => setReason(e.target.value)} placeholder={activating ? 'Invoice paid' : 'Unpaid invoice #1042'} /></Field>
    </Modal>
  );
}

function RenameModal({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: (m: string) => void }) {
  const [name, setName] = useState(d.tenant.company_name);
  const { busy, err, submit } = useSubmit(onDone);
  return (
    <Modal open title="Rename workspace" onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={busy} disabled={name.trim().length < 2 || name.trim() === d.tenant.company_name}
        onClick={() => submit(async () => { await api.patch(`/tenants/${d.tenant.id}`, { company_name: name.trim() }); return 'Workspace renamed'; })}>Save</Button></>}>
      <Field label="Company name" error={err}><Input value={name} onChange={e => setName(e.target.value)} /></Field>
      <p className="mt-2 text-xs text-slate-500"><BellRing className="mr-1 inline h-3 w-3" />The new name shows across FluxWatch for this tenant straight away.</p>
    </Modal>
  );
}
